import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { BaseService } from '../base.service';
import { UserAuthService } from '../auth.service';
import { SPECTRUM_API_PATH, SPECTRUM_AUTH_API } from '../tokens';

export interface SpectrumModule {
    moduleName: string;
    route: string;
    image?: string;
    /** Set on Group Admin entries: launch the module scoped to this group. */
    groupCode?: string;
    groupName?: string;
}

interface GroupAppAccess {
    orgCode?: string;
    orgName?: string;
}

/**
 * The list of Spectrum modules the logged-in user can reach, plus the
 * cross-module launch. Shared by the header bar and the sidebar switcher so
 * both show the same entries from a single `security/modules` call per app load.
 */
@Injectable({ providedIn: 'root' })
export class SpectrumModuleService {

    private auth = inject(UserAuthService);
    private base = inject(BaseService);
    private apiPath = inject(SPECTRUM_API_PATH);
    private authBase = inject(SPECTRUM_AUTH_API);

    private modules$?: Observable<SpectrumModule[]>;
    private myProfileRoute = '';

    getModules(): Observable<SpectrumModule[]> {
        if (!this.modules$) {
            this.modules$ = this.base.get<SpectrumModule[]>(`${this.apiPath}/security/modules`).pipe(
                switchMap(modules => this.decorate(modules || [])),
                catchError(() => of<SpectrumModule[]>([])),
                shareReplay(1)
            );
        }
        return this.modules$;
    }

    /** The module whose route matches the current origin, if any. */
    currentModule(modules: SpectrumModule[]): SpectrumModule | null {
        const origin = this.normalizeRoute(window.location.origin);
        return modules.find(module => origin === this.normalizeRoute(module.route)) || null;
    }

    isCurrent(module: SpectrumModule, modules: SpectrumModule[]): boolean {
        const current = this.currentModule(modules);
        return this.normalizeRoute(current?.route) === this.normalizeRoute(module.route)
            && current?.moduleName === module.moduleName;
    }

    /** Hands the current session to another module through its `/validate` bootstrap. */
    switchModule(module: SpectrumModule): void {
        if (!module.route) return;
        const params = this.buildSessionParams();
        if (module.groupCode) {
            // Group Admin runs against the group, not the current school —
            // rewrite the session scope so its header and APIs use the group.
            params.set('code', module.groupCode);
            params.set('appName', 'GroupAdmin');
            if (module.groupName) {
                params.set('schoolName', module.groupName);
            }
        }
        window.location.href = `${module.route}/validate?${params.toString()}`;
    }

    openMyProfile(): void {
        const route = this.myProfileRoute || `https://myprofile.${new URL(this.authBase).hostname}`;
        window.location.href = `${route}/validate?${this.buildSessionParams().toString()}`;
    }

    buildSessionParams(): URLSearchParams {
        const params = new URLSearchParams();
        const token = this.auth.getToken();
        const orgCode = this.auth.getOrgCode();
        const appName = this.auth.getAppName() || 'Spectrum';
        const employeeId = this.auth.getEmployeeId();
        const employeeName = this.auth.getEmployeeName();
        const schoolName = this.auth.getSchoolName();
        const userName = this.auth.getUserName();

        if (token) params.set('token', token);
        if (orgCode) params.set('code', orgCode);
        params.set('appName', appName);
        if (employeeId) params.set('employeeId', employeeId);
        if (employeeName) params.set('employeeName', employeeName);
        if (schoolName) params.set('schoolName', schoolName);
        if (userName) params.set('userName', userName);

        return params;
    }

    normalizeRoute(route: string | undefined | null): string {
        return (route || '').trim().replace(/\/+$/, '');
    }

    private decorate(raw: SpectrumModule[]): Observable<SpectrumModule[]> {
        const profileModule = raw.find(module =>
            module.moduleName === 'MyProfile' || module.moduleName === 'My Profile'
        );
        this.myProfileRoute = profileModule?.route || `https://myprofile.${new URL(this.authBase).hostname}`;

        const modules = raw
            .filter(module => !['MyProfile', 'My Profile'].includes(module.moduleName))
            .map(module => ({
                ...module,
                moduleName: module.moduleName === 'School' ? 'Institute' : module.moduleName
            }));

        return this.gateGroupAdmin(modules);
    }

    /**
     * Group Admin is not a school-scoped module: it must appear only when the
     * logged-in user actually belongs to at least one group — i.e. holds a
     * (GroupAdmin, groupCd) access row in auth. The row also supplies the
     * group code/name the launch needs (a school-scoped launch would start a
     * broken group session against the school code).
     */
    private gateGroupAdmin(modules: SpectrumModule[]): Observable<SpectrumModule[]> {
        const groupEntries = modules.filter(module => module.moduleName === 'Group Admin');
        if (groupEntries.length === 0) {
            return of(modules);
        }
        const template = groupEntries[0];
        const rest = modules.filter(module => module.moduleName !== 'Group Admin');

        const username = this.auth.getUserName();
        if (!username) {
            return of(rest);
        }
        const params = new URLSearchParams({ username, appName: 'GroupAdmin' });
        return this.base.get<GroupAppAccess[]>(`${this.authBase}/auth/user-access?${params.toString()}`).pipe(
            map(rows => {
                const groups = (rows || []).filter(row => row.orgCode);
                if (groups.length === 0) {
                    return rest;
                }
                const entries = groups.map(row => ({
                    ...template,
                    moduleName: groups.length > 1
                        ? `Group Admin — ${row.orgName || row.orgCode}`
                        : 'Group Admin',
                    groupCode: row.orgCode,
                    groupName: row.orgName
                }));
                return [...rest, ...entries];
            }),
            catchError(() => of(rest))
        );
    }
}
